/**
 * Standard base64 (RFC 4648 §4, padded) for the embedded Yjs states and updates
 * (PLAN §6.1: `text { script?: <yjs state b64> }`).
 *
 * Hand-written because this package has no platform types: `Buffer` is Node,
 * `btoa`/`atob` are DOM globals and work on binary strings, not bytes. A codec
 * is thirty lines, and one definition keeps every host byte-identical.
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
export const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

/** Whether `value` is canonical padded base64. The empty string is valid (zero bytes). */
export function isValidBase64(value: string): boolean {
  return BASE64_PATTERN.test(value);
}

/** Encode bytes as padded base64. */
export function encodeBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1] ?? 0;
    const c = bytes[i + 2] ?? 0;
    const triple = (a << 16) | (b << 8) | c;
    const remaining = bytes.length - i;
    out += ALPHABET.charAt((triple >> 18) & 63) + ALPHABET.charAt((triple >> 12) & 63);
    out += remaining > 1 ? ALPHABET.charAt((triple >> 6) & 63) : '=';
    out += remaining > 2 ? ALPHABET.charAt(triple & 63) : '=';
  }
  return out;
}

/** Decode padded base64, or null when `value` is not canonical base64. */
export function decodeBase64(value: string): Uint8Array | null {
  if (!isValidBase64(value)) return null;
  const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
  const out = new Uint8Array((value.length / 4) * 3 - padding);
  let o = 0;
  for (let i = 0; i < value.length; i += 4) {
    let triple = 0;
    for (let j = 0; j < 4; j += 1) {
      const index = ALPHABET.indexOf(value.charAt(i + j));
      triple = (triple << 6) | (index < 0 ? 0 : index);
    }
    for (const shift of [16, 8, 0]) {
      if (o < out.length) {
        out[o] = (triple >> shift) & 255;
        o += 1;
      }
    }
  }
  return out;
}
